/**
 * PKCS#7/CMS certificate bags and SignedData.
 *
 * Creates degenerate (signature-less) certificate bags, parses RFC 5652
 * SignedData (and the RFC 2315 form whose contentInfo content is an OCTET
 * STRING), and verifies signer signatures including signed-attribute flows.
 * A CertificateSet is decoded as RFC 5652 §10.2.2 CertificateChoices, so an
 * attribute certificate or other-format entry is preserved rather than dropped.
 *
 * @module
 */

import {
	childrenOf,
	decodeIntegerNumber,
	decodeObjectIdentifier,
	decodeString,
	hexToBytes,
	requireElement,
	toArrayBuffer,
	toHex,
} from '#micro509/internal/asn1/asn1';
import type { DerElement } from '#micro509/internal/asn1/der';
import {
	DEFAULT_MAX_DER_DEPTH,
	explicitContext,
	integer,
	integerFromNumber,
	objectIdentifier,
	octetString,
	readElement,
	readRootElement,
	readSequenceChildren,
	sequence,
	setOf,
} from '#micro509/internal/asn1/der';
import { OIDS } from '#micro509/internal/asn1/oids';
import {
	describeHashAlgorithm,
	describeSignatureAlgorithm,
} from '#micro509/internal/crypto/algorithm-names';
import {
	getVerifySignatureConfigResult,
	verifySignedDataDetailed,
} from '#micro509/internal/crypto/sig-verify';
import type { SignatureAlgorithmIdentifier } from '#micro509/internal/crypto/signing';
import {
	encodeAlgorithmIdentifier,
	getSignatureAlgorithm,
	signBytes,
} from '#micro509/internal/crypto/signing';
import { getCrypto } from '#micro509/internal/crypto/webcrypto';
import { base64Encode } from '#micro509/internal/shared/base64';
import { compareDistinguishedNames } from '#micro509/internal/shared/dn';
import { pemEncode, splitPemBlocksOrThrow } from '#micro509/pem/pem';
import type { ErrorResult, Micro509Error } from '#micro509/result/result';
import { failureResult } from '#micro509/result/result';
import type { SignatureProfileInput } from '#micro509/x509/certificate';
import type { NameFieldKey } from '#micro509/x509/name';
import { nameFieldKeyFromOid } from '#micro509/x509/name';
import type {
	ParsedCertificate,
	ParsedName,
	ParsedNameAttribute,
	ParsedRelativeDistinguishedName,
} from '#micro509/x509/parse';
import { parseCertificateDerOrThrow } from '#micro509/x509/parse';

/** PEM text (may contain multiple CERTIFICATE blocks), raw DER bytes, or an already-parsed certificate. */
export type Pkcs7CertificateSource = string | Uint8Array | ParsedCertificate;

/** RFC 7468 Section 8 armors an RFC 2315 ContentInfo; Section 9 armors an RFC 5652 one. */
const CONTENT_INFO_PEM_LABELS = ['PKCS7', 'CMS'] as const;

/** DER, PEM, and base64 encodings of a PKCS#7 certificate bag. */
export interface Pkcs7CertBagMaterial {
	/** Raw DER-encoded PKCS#7 structure. */
	readonly der: Uint8Array;
	/** PEM-armored PKCS#7 (`-----BEGIN PKCS7-----`). */
	readonly pem: string;
	/** Base64-encoded DER (no PEM armor). */
	readonly base64: string;
}

/**
 * RFC 5652 §5.3 `SignerIdentifier ::= CHOICE { issuerAndSerialNumber,
 * subjectKeyIdentifier [0] }`, which locates the signer's certificate.
 */
export type ParsedSignerIdentifier =
	| {
			/** Issuer name plus serial number. */
			readonly type: 'issuerAndSerialNumber';
			/** Parsed issuer distinguished name. */
			readonly issuer: ParsedName;
			/** Hex-encoded certificate serial number. */
			readonly serialNumberHex: string;
	  }
	| {
			/** SubjectKeyIdentifier (`[0]`). */
			readonly type: 'subjectKeyIdentifier';
			/** Hex-encoded SubjectKeyIdentifier of the signer certificate. */
			readonly subjectKeyIdentifier: string;
	  };

/** Fields shared by every decoded SignerInfo, regardless of signed-attribute presence. */
export interface ParsedPkcs7SignerInfoBase {
	/** CMS SignerInfo version (typically 1 for issuerAndSerialNumber). */
	readonly version: number;
	/** Which of the two RFC 5652 §5.3 SignerIdentifier alternatives this SignerInfo uses. */
	readonly signerIdentifier: ParsedSignerIdentifier;
	/** OID of the digest algorithm used to hash the content. */
	readonly digestAlgorithmOid: string;
	/** Human-readable digest algorithm name (e.g. `"SHA-256"`). */
	readonly digestAlgorithmName: string;
	/** OID of the algorithm used to produce the signature. */
	readonly signatureAlgorithmOid: string;
	/** Human-readable signature algorithm name. */
	readonly signatureAlgorithmName: string;
	/** Raw DER of the signature AlgorithmIdentifier parameters, if present. */
	readonly signatureAlgorithmParametersDer?: Uint8Array;
	/** Hex-encoded raw signature bytes. */
	readonly signatureHex: string;
	/** Raw signature bytes. */
	readonly signature: Uint8Array;
}

/**
 * A single SignerInfo decoded from a PKCS#7 SignedData structure.
 *
 * Discriminated on `hasSignedAttrs`: when `true`, `signedAttrsDer` is always
 * present; when `false`, it cannot exist.
 */
export type ParsedPkcs7SignerInfo =
	| (ParsedPkcs7SignerInfoBase & {
			/** This SignerInfo includes authenticated (signed) attributes. */
			readonly hasSignedAttrs: true;
			/** Raw DER of signedAttrs with original IMPLICIT [0] tag (0xa0). */
			readonly signedAttrsDer: Uint8Array;
	  })
	| (ParsedPkcs7SignerInfoBase & {
			/** This SignerInfo has no authenticated attributes. */
			readonly hasSignedAttrs: false;
			/** Never present without signed attributes. */
			readonly signedAttrsDer?: undefined;
	  });

interface ParsedSignedDataOptionalFields {
	readonly certificates?: DerElement;
	readonly crls?: DerElement;
}

interface ParsedSignedAttributeFields {
	messageDigest?: Uint8Array;
	contentTypeOid?: string;
}

/**
 * One RFC 5652 §10.2.2 CertificateChoices alternative.
 *
 * X.509 is the only alternative this library decodes. The rest keep their DER,
 * including the context tag, so a CertificateSet round-trips and a caller can
 * tell an X.509-only bag from one carrying attribute certificates. RFC 5652
 * marks `extendedCertificate` and `attributeCertificateV1` obsolete.
 */
export type ParsedCertificateChoice =
	| {
			/** X.509 certificate (untagged `Certificate`). */
			readonly type: 'certificate';
			/** The decoded certificate. */
			readonly certificate: ParsedCertificate;
	  }
	| {
			/** PKCS #6 extended certificate (`[0]`), obsolete. */
			readonly type: 'extendedCertificate';
			/** Raw DER of the element, including its context tag. */
			readonly der: Uint8Array;
	  }
	| {
			/** Version 1 X.509 attribute certificate (`[1]`), obsolete. */
			readonly type: 'attributeCertificateV1';
			/** Raw DER of the element, including its context tag. */
			readonly der: Uint8Array;
	  }
	| {
			/** Version 2 X.509 attribute certificate (`[2]`). */
			readonly type: 'attributeCertificateV2';
			/** Raw DER of the element, including its context tag. */
			readonly der: Uint8Array;
	  }
	| {
			/** Any other certificate format (`[3] OtherCertificateFormat`). */
			readonly type: 'other';
			/** `otherCertFormat` OID identifying the format. */
			readonly formatOid: string;
			/** Raw DER of the element, including its context tag. */
			readonly der: Uint8Array;
	  };

/** Decoded PKCS#7 SignedData content, including certificates and signer info. */
export interface ParsedPkcs7SignedData {
	/** Original DER bytes when this object came from {@linkcode parsePkcs7SignedDataDer} or PEM parsing. */
	readonly der?: Uint8Array;
	/** Outer ContentInfo type OID (always `pkcs7-signedData`). */
	readonly contentTypeOid: string;
	/** SignedData version number. */
	readonly version: number;
	/** OIDs of digest algorithms declared in `digestAlgorithms`. */
	readonly digestAlgorithmOids: readonly string[];
	/** Human-readable digest algorithm names declared in `digestAlgorithms`. */
	readonly digestAlgorithmNames: readonly string[];
	/** OID of the encapsulated content type (e.g. `pkcs7-data`). */
	readonly encapsulatedContentTypeOid: string;
	/** Raw encapsulated content bytes. Absent in degenerate (certs-only) bags. */
	readonly encapsulatedContent?: Uint8Array;
	/** RFC 5652 §10.2.2 CertificateChoices entries from the SignedData certificate set. */
	readonly certificateChoices: readonly ParsedCertificateChoice[];
	/** Decoded signer info entries. Empty for degenerate cert bags. */
	readonly signerInfos: readonly ParsedPkcs7SignerInfo[];
}

// Result types for PKCS#7 parsing

/** Error codes for PKCS#7 parse failures. */
export type ParsePkcs7ErrorCode = 'malformed' | 'not_signed_data';

/** Error payload for a failed PKCS#7 parse. */
export interface ParsePkcs7Failure extends Micro509Error<ParsePkcs7ErrorCode> {
	/** Always `false` for failures. */
	readonly ok: false;
}

/** Success-or-failure result from {@linkcode parsePkcs7SignedDataDer} / {@linkcode parsePkcs7SignedDataPem}. */
export type ParsePkcs7SignedDataResult =
	| {
			/** Parse succeeded. */
			readonly ok: true;
			/** Decoded SignedData. */
			readonly value: ParsedPkcs7SignedData;
	  }
	| ErrorResult<ParsePkcs7ErrorCode, Record<never, never>, ParsePkcs7Failure>;

/** Success-or-failure result from {@linkcode parsePkcs7CertBagDer} / {@linkcode parsePkcs7CertBagPem}. */
export type ParsePkcs7CertBagResult =
	| {
			/** Parse succeeded. */
			readonly ok: true;
			/** Parsed certificates from the cert bag. */
			readonly value: readonly ParsedCertificate[];
	  }
	| ErrorResult<ParsePkcs7ErrorCode, Record<never, never>, ParsePkcs7Failure>;

/**
 * Error codes for {@linkcode verifyPkcs7SignedData} failures.
 *
 * `detached_content_required` means the SignedData carries no `eContent`
 * (detached signature or degenerate cert bag) and no external content was
 * supplied via {@linkcode VerifyPkcs7SignedDataOptions}.
 */
export type VerifyPkcs7SignedDataErrorCode =
	| 'signer_not_found'
	| 'signature_invalid'
	| 'message_digest_mismatch'
	| 'detached_content_required'
	| ParsePkcs7ErrorCode;

/** Error payload for a failed {@linkcode verifyPkcs7SignedData} call. */
export interface VerifyPkcs7SignedDataFailure
	extends Micro509Error<VerifyPkcs7SignedDataErrorCode> {
	/** Always `false` for failures. */
	readonly ok: false;
}

/** Options for {@linkcode verifyPkcs7SignedData}. */
export interface VerifyPkcs7SignedDataOptions {
	/**
	 * External content for a detached SignedData (RFC 5652 Section 5.2, absent `eContent`).
	 * Required to verify a detached signature; ignored when the SignedData embeds its own content.
	 */
	readonly content?: Uint8Array;
}

/** A SignerInfo paired with the certificate that verified its signature. */
export interface VerifiedPkcs7Signer {
	/** The SignerInfo whose signature verified. */
	readonly signerInfo: ParsedPkcs7SignerInfo;
	/** The certificate from the SignedData certificate set that verified it. */
	readonly certificate: ParsedCertificate;
}

/** Success-or-failure result from {@linkcode verifyPkcs7SignedData}. */
export type VerifyPkcs7SignedDataResult =
	| {
			/** Verification succeeded. */
			readonly ok: true;
			/** The verified SignedData structure. */
			readonly value: ParsedPkcs7SignedData;
			/** One entry per SignerInfo, in SignedData order. */
			readonly signers: readonly VerifiedPkcs7Signer[];
	  }
	| ErrorResult<VerifyPkcs7SignedDataErrorCode, Record<never, never>, VerifyPkcs7SignedDataFailure>;

// createPkcs7CertBag

/** Caller-correctable failure code from {@linkcode createPkcs7CertBag}. */
export type CreatePkcs7CertBagErrorCode = 'invalid_certificate';

/** Error payload for a failed PKCS#7 certificate bag creation. */
export interface CreatePkcs7CertBagFailure extends Micro509Error<CreatePkcs7CertBagErrorCode> {
	/** Always `false` for failures. */
	readonly ok: false;
}

/** Success-or-failure result from {@linkcode createPkcs7CertBag}. */
export type CreatePkcs7CertBagResult =
	| {
			/** Creation succeeded. */
			readonly ok: true;
			/** DER, PEM, and base64 forms of the certificate bag. */
			readonly value: Pkcs7CertBagMaterial;
	  }
	| ErrorResult<CreatePkcs7CertBagErrorCode, Record<never, never>, CreatePkcs7CertBagFailure>;

/**
 * Creates a degenerate PKCS#7 SignedData structure containing only
 * certificates (no signers), returning DER, PEM, and base64 forms, or a
 * typed `invalid_certificate` failure when a certificate source is not
 * valid PEM/DER.
 */
export function createPkcs7CertBag(
	certificates: readonly Pkcs7CertificateSource[],
): CreatePkcs7CertBagResult {
	// normalizeCertificateSource parses untrusted PEM/DER and throws on malformed
	// input — a caller-correctable trust boundary, so map it to a typed failure.
	let certificateDers: Uint8Array[];
	try {
		certificateDers = certificates.flatMap(normalizeCertificateSource);
		for (const der of certificateDers) {
			parseCertificateDerOrThrow(der);
		}
	} catch {
		return createCertBagFailure(
			'invalid_certificate',
			'Each PKCS#7 certificate source must be valid PEM or DER',
		);
	}
	// certificates [0] IMPLICIT CertificateSet — a DER SET OF must be canonically
	// ordered, so sort via setOf, then retag 0x31 -> 0xa0 for the IMPLICIT [0].
	const certificateSet = new Uint8Array(setOf(certificateDers));
	certificateSet[0] = 0xa0;
	const signedData = sequence([
		integerFromNumber(1),
		setOf([]),
		sequence([objectIdentifier(OIDS.pkcs7Data)]),
		certificateSet,
		setOf([]),
	]);
	const der = sequence([objectIdentifier(OIDS.pkcs7SignedData), explicitContext(0, signedData)]);
	return {
		ok: true,
		value: { der, pem: pemEncode('PKCS7', der), base64: base64Encode(der) },
	};
}

// createPkcs7SignedData

/** A single signer for {@linkcode createPkcs7SignedData}. */
export interface Pkcs7Signer {
	/**
	 * Signer certificate (PEM text with one CERTIFICATE block, or raw DER).
	 * Embedded in the SignedData certificate set and referenced by the
	 * SignerInfo via issuerAndSerialNumber.
	 */
	readonly certificate: Pkcs7CertificateSource;
	/** Private key matching the certificate's public key, used to sign. */
	readonly privateKey: CryptoKey;
	/**
	 * Signature profile. Defaults to inferring the algorithm from the key
	 * (e.g. ECDSA→ecdsa-with-SHA*, RSA→sha*WithRSAEncryption, Ed25519).
	 * Pass `{ kind: 'rsa-pss' }` to force RSA-PSS padding for an RSA-PSS key.
	 */
	readonly signature?: SignatureProfileInput;
}

/** Input for {@linkcode createPkcs7SignedData}. */
export interface CreatePkcs7SignedDataInput {
	/** Content to encapsulate and sign (the eContent). */
	readonly content: Uint8Array;
	/** One or more signers. Each produces a SignerInfo with signed attributes. */
	readonly signers: readonly Pkcs7Signer[];
	/**
	 * Additional certificates to embed (e.g. intermediates). Signer
	 * certificates are always embedded; duplicate DER is removed.
	 */
	readonly additionalCertificates?: readonly Pkcs7CertificateSource[];
	/**
	 * Encapsulated content type OID.
	 * @default `'1.2.840.113549.1.7.1'` (pkcs7-data)
	 */
	readonly encapsulatedContentTypeOid?: string;
	/**
	 * Omit `eContent` from `encapContentInfo` (RFC 5652 Section 5.2 detached
	 * form). The signature still covers `content` via the messageDigest signed
	 * attribute, but the bytes are not embedded — the verifier must supply them
	 * externally (e.g. git x509 commit signing, S/MIME detached signatures).
	 * @default false
	 */
	readonly detached?: boolean;
}

/** DER, PEM, and base64 encodings of a PKCS#7/CMS SignedData structure. */
export interface Pkcs7SignedDataMaterial {
	/** Raw DER-encoded SignedData ContentInfo. */
	readonly der: Uint8Array;
	/**
	 * PEM-armored ContentInfo. A version 1 SignedData carries the `PKCS7`
	 * label; a version 3 one is outside RFC 2315, whose SignedData version
	 * "shall be 1", so it carries the RFC 7468 Section 9 `CMS` label.
	 */
	readonly pem: string;
	/** Base64-encoded DER (no PEM armor). */
	readonly base64: string;
}

/** Caller-correctable failure codes from {@linkcode createPkcs7SignedData}. */
export type CreatePkcs7SignedDataErrorCode =
	| 'no_signers'
	| 'invalid_signer_certificate'
	| 'invalid_certificate'
	| 'signer_certificate_key_mismatch'
	| 'unsupported_signer_key';

/** Error payload for a failed PKCS#7 SignedData creation. */
export interface CreatePkcs7SignedDataFailure
	extends Micro509Error<CreatePkcs7SignedDataErrorCode> {
	/** Always `false` for failures. */
	readonly ok: false;
}

/** Success-or-failure result from {@linkcode createPkcs7SignedData}. */
export type CreatePkcs7SignedDataResult =
	| {
			/** Creation succeeded. */
			readonly ok: true;
			/** DER, PEM, and base64 forms of the SignedData. */
			readonly value: Pkcs7SignedDataMaterial;
	  }
	| ErrorResult<CreatePkcs7SignedDataErrorCode, Record<never, never>, CreatePkcs7SignedDataFailure>;

/**
 * Creates a PKCS#7/CMS SignedData with one or more signers over `content`.
 *
 * Each signer uses the RFC 5652 Section 5.4 signed-attributes flow: the
 * signature covers a `SET OF` authenticated attributes carrying `contentType`
 * and `messageDigest` (the digest of the encapsulated content). By default the
 * content is embedded (attached signature), so the result verifies with
 * {@linkcode verifyPkcs7SignedData} without any external data. With
 * `detached: true` the `eContent` is omitted (RFC 5652 Section 5.2) and the
 * verifier must supply the content externally.
 *
 * The content digest is derived from each signer's key (P-256/RSA-SHA256 →
 * SHA-256, P-384 → SHA-384, P-521 → SHA-512, Ed25519 → SHA-512 per RFC 8419).
 *
 * Returns a {@linkcode CreatePkcs7SignedDataResult}: DER, PEM, and base64
 * forms on success, or a typed failure for caller-correctable input (no
 * signers, a signer source that is not exactly one certificate, a signer
 * certificate whose public key cannot verify the signer key's algorithm, or an
 * unsupported signer key).
 */
export async function createPkcs7SignedData(
	input: CreatePkcs7SignedDataInput,
): Promise<CreatePkcs7SignedDataResult> {
	if (input.signers.length === 0) {
		return createPkcs7Failure('no_signers', 'createPkcs7SignedData requires at least one signer');
	}
	const encapsulatedContentTypeOid = input.encapsulatedContentTypeOid ?? OIDS.pkcs7Data;

	// Snapshot the caller-owned content into a private copy before any await.
	// Every signer's messageDigest and the emitted eContent must derive from the
	// same bytes; reading input.content across awaits could otherwise observe a
	// caller mutation and yield a SignedData whose digest disagrees with its own
	// encapsulated content.
	const content = input.content.slice();

	const certificateDers: Uint8Array[] = [];
	const seenCertificates = new Set<string>();
	const addCertificate = (der: Uint8Array): void => {
		const hex = toHex(der);
		if (!seenCertificates.has(hex)) {
			seenCertificates.add(hex);
			certificateDers.push(der);
		}
	};

	const digestAlgorithmOids = new Set<string>();
	const signerInfos: Uint8Array[] = [];
	for (const signer of input.signers) {
		const built = await buildPkcs7SignerInfo(signer, content, encapsulatedContentTypeOid);
		if (!built.ok) return built;
		addCertificate(built.certificateDer);
		digestAlgorithmOids.add(built.digestOid);
		signerInfos.push(built.signerInfoDer);
	}

	for (const source of input.additionalCertificates ?? []) {
		try {
			for (const der of normalizeCertificateSource(source)) {
				parseCertificateDerOrThrow(der);
				addCertificate(der);
			}
		} catch {
			return createPkcs7Failure(
				'invalid_certificate',
				'Each additional PKCS#7 certificate source must be valid PEM or DER',
			);
		}
	}

	// SignedData version: 1 for id-data content, otherwise 3 (RFC 5652 Section 5.1).
	const signedDataVersion = encapsulatedContentTypeOid === OIDS.pkcs7Data ? 1 : 3;
	// certificates [0] IMPLICIT CertificateSet — a DER SET OF must be canonically
	// ordered, so sort via setOf, then retag 0x31 -> 0xa0 for the IMPLICIT [0].
	const certificateSet = new Uint8Array(setOf(certificateDers));
	certificateSet[0] = 0xa0;
	const signedData = sequence([
		integerFromNumber(signedDataVersion),
		setOf([...digestAlgorithmOids].map((oid) => sequence([objectIdentifier(oid)]))),
		input.detached === true
			? sequence([objectIdentifier(encapsulatedContentTypeOid)])
			: sequence([
					objectIdentifier(encapsulatedContentTypeOid),
					explicitContext(0, octetString(content)),
				]),
		certificateSet,
		setOf(signerInfos),
	]);
	const der = sequence([objectIdentifier(OIDS.pkcs7SignedData), explicitContext(0, signedData)]);
	// RFC 7468 Section 8 requires PKCS7-labelled data to be an RFC 2315
	// ContentInfo, whose SignedData version "shall be 1"; version 3 is described
	// only by RFC 5652, which Section 9 armors as CMS.
	const label = signedDataVersion === 1 ? 'PKCS7' : 'CMS';
	return {
		ok: true,
		value: { der, pem: pemEncode(label, der), base64: base64Encode(der) },
	};
}

/** One built SignerInfo plus what the SignedData envelope needs from it. */
type Pkcs7SignerInfoBuild =
	| {
			/** Build succeeded. */
			readonly ok: true;
			/** DER-encoded SignerInfo. */
			readonly signerInfoDer: Uint8Array;
			/** Digest algorithm OID for the digestAlgorithms SET. */
			readonly digestOid: string;
			/** Signer certificate DER for the certificate set. */
			readonly certificateDer: Uint8Array;
	  }
	| ErrorResult<CreatePkcs7SignedDataErrorCode, Record<never, never>, CreatePkcs7SignedDataFailure>;

/**
 * Builds one signer's SignerInfo over `content` (RFC 5652 Section 5.4
 * signed-attributes flow). Every throwing step here reacts to
 * caller-correctable input — malformed PEM/DER, unsupported key — so each is
 * mapped to a typed failure rather than rejecting the public Promise.
 */
async function buildPkcs7SignerInfo(
	signer: Pkcs7Signer,
	content: Uint8Array,
	encapsulatedContentTypeOid: string,
): Promise<Pkcs7SignerInfoBuild> {
	// normalizeCertificateSource throws on malformed PEM (stray or truncated markers).
	let signerCertDers: readonly Uint8Array[];
	try {
		signerCertDers = normalizeCertificateSource(signer.certificate);
	} catch {
		return createPkcs7Failure(
			'invalid_signer_certificate',
			'Each PKCS#7 signer certificate source must be valid PEM or DER',
		);
	}
	const signerCertDer = signerCertDers[0];
	if (signerCertDer === undefined || signerCertDers.length !== 1) {
		return createPkcs7Failure(
			'invalid_signer_certificate',
			'Each PKCS#7 signer must provide exactly one certificate',
		);
	}
	let certificate: ParsedCertificate;
	try {
		certificate = parseCertificateDerOrThrow(signerCertDer);
	} catch {
		return createPkcs7Failure(
			'invalid_signer_certificate',
			'Each PKCS#7 signer certificate must be a parseable X.509 certificate',
		);
	}
	// getSignatureAlgorithm throws only for unsupported/misconfigured keys.
	let signatureAlgorithm: SignatureAlgorithmIdentifier;
	try {
		signatureAlgorithm = getSignatureAlgorithm(signer.privateKey, signer.signature);
	} catch {
		return createPkcs7Failure(
			'unsupported_signer_key',
			'Unsupported signer key or signature profile',
		);
	}
	// RFC 8410 §12: "the same public key cannot be used for both ECDH and EdDSA",
	// so a SignerInfo must not name a certificate whose subject public key cannot
	// verify the algorithm the signer key produces.
	if (
		!getVerifySignatureConfigResult(
			signatureAlgorithm.algorithmOid,
			signatureAlgorithm.parameters,
			certificate.publicKeyAlgorithmOid,
			certificate.publicKeyParametersOid,
			'signer',
		).ok
	) {
		return createPkcs7Failure(
			'signer_certificate_key_mismatch',
			'Each PKCS#7 signer certificate must carry a public key that verifies the signer key algorithm',
		);
	}
	const digest = contentDigestForPrivateKey(signer.privateKey);
	if (digest === undefined) {
		return createPkcs7Failure(
			'unsupported_signer_key',
			'Unsupported signer key algorithm for content digest',
		);
	}
	const messageDigest = new Uint8Array(
		await getCrypto().subtle.digest(digest.hashName, toArrayBuffer(content)),
	);
	const { setForSigning, implicitForEmit } = buildSignedAttributes(
		encapsulatedContentTypeOid,
		messageDigest,
	);
	let signature: Uint8Array;
	try {
		signature = await signBytes(signer.privateKey, signatureAlgorithm, setForSigning);
	} catch {
		return createPkcs7Failure(
			'unsupported_signer_key',
			'Signer key could not produce a PKCS#7 signature',
		);
	}
	return {
		ok: true,
		signerInfoDer: sequence([
			integerFromNumber(1),
			sequence([
				hexToBytes(certificate.issuer.derHex),
				integer(hexToBytes(certificate.serialNumberHex)),
			]),
			sequence([objectIdentifier(digest.digestOid)]),
			implicitForEmit,
			encodeAlgorithmIdentifier(signatureAlgorithm),
			octetString(signature),
		]),
		digestOid: digest.digestOid,
		certificateDer: signerCertDer,
	};
}

// parsePkcs7CertBag — Result-returning

/** Parses a DER-encoded PKCS#7 cert bag, returning the contained certificates. */
export function parsePkcs7CertBagDer(der: Uint8Array): ParsePkcs7CertBagResult {
	const result = parsePkcs7SignedDataDer(der);
	if (!result.ok) {
		return result;
	}
	return { ok: true, value: x509CertificatesOf(result.value.certificateChoices) };
}

/** The DER of the single `PKCS7` or `CMS` block in `pem`, or a typed failure. */
function contentInfoPemBytes(
	pem: string,
):
	| { readonly ok: true; readonly bytes: Uint8Array }
	| ErrorResult<ParsePkcs7ErrorCode, Record<never, never>, ParsePkcs7Failure> {
	try {
		const blocks = splitPemBlocksOrThrow(pem).filter((block) =>
			CONTENT_INFO_PEM_LABELS.some((label) => label === block.label),
		);
		const block = blocks[0];
		if (block === undefined || blocks.length !== 1) {
			return pkcs7Failure('malformed', 'Expected exactly one PKCS7 or CMS PEM block');
		}
		return { ok: true, bytes: block.bytes };
	} catch {
		return pkcs7Failure('malformed', 'Expected exactly one PKCS7 or CMS PEM block');
	}
}

/** Parses a PEM-armored PKCS#7/CMS cert bag. Expects exactly one `PKCS7` or `CMS` PEM block. */
export function parsePkcs7CertBagPem(pem: string): ParsePkcs7CertBagResult {
	const block = contentInfoPemBytes(pem);
	if (!block.ok) return block;
	return parsePkcs7CertBagDer(block.bytes);
}

// parsePkcs7SignedData — Result-returning

/** Decodes a DER-encoded PKCS#7 ContentInfo expecting `signedData` content type. */
export function parsePkcs7SignedDataDer(der: Uint8Array): ParsePkcs7SignedDataResult {
	try {
		const contentInfo = readSequenceChildren(der, { maxDepth: DEFAULT_MAX_DER_DEPTH });
		const contentType = contentInfo[0];
		const content = contentInfo[1];
		if (contentType === undefined || contentInfo.length > 2 || contentType.tag !== 0x06) {
			return pkcs7Failure('malformed', 'Malformed PKCS#7 content info');
		}
		if (content !== undefined && (content.tag !== 0xa0 || childrenOf(der, content).length !== 1)) {
			return pkcs7Failure('malformed', 'Malformed PKCS#7 content info');
		}
		const contentTypeOid = decodeObjectIdentifier(contentType.value);
		if (contentTypeOid !== OIDS.pkcs7SignedData) {
			return pkcs7Failure('not_signed_data', 'PKCS#7 content is not signedData');
		}
		if (content === undefined) {
			return pkcs7Failure('malformed', 'PKCS#7 signedData content info carries no content');
		}
		const signedData = childAt(der, content, 0, 'signedData');
		const signedDataChildren = childrenOf(der, signedData);
		const version = signedDataChildren[0];
		const digestAlgorithms = signedDataChildren[1];
		const encapContentInfo = signedDataChildren[2];
		const trailingChildren = signedDataChildren.slice(3);
		const signerInfos = trailingChildren[trailingChildren.length - 1];
		const optionalFields = parseSignedDataOptionalFields(trailingChildren.slice(0, -1));
		if (!optionalFields.ok) return optionalFields;
		if (
			version === undefined ||
			digestAlgorithms === undefined ||
			encapContentInfo === undefined ||
			signerInfos === undefined ||
			signerInfos.tag !== 0x31
		) {
			return pkcs7Failure('malformed', 'Malformed SignedData');
		}
		const encapDer = der.slice(
			encapContentInfo.start - encapContentInfo.headerLength,
			encapContentInfo.end,
		);
		const encapChildren = readSequenceChildren(encapDer);
		const encapType = encapChildren[0];
		const encapContent = encapChildren[1];
		const digestAlgorithmOids = parseDigestAlgorithms(der, digestAlgorithms);
		if (encapType === undefined || encapChildren.length > 2 || encapType.tag !== 0x06) {
			return pkcs7Failure('malformed', 'Malformed EncapsulatedContentInfo');
		}
		return {
			ok: true,
			value: {
				der: new Uint8Array(der),
				contentTypeOid,
				version: decodeIntegerNumber(version.value),
				digestAlgorithmOids,
				digestAlgorithmNames: digestAlgorithmOids.map((oid) => describeHashAlgorithm(oid)),
				encapsulatedContentTypeOid: decodeObjectIdentifier(encapType.value),
				...(encapContent === undefined
					? {}
					: {
							encapsulatedContent: extractEncapsulatedContent(encapDer, encapContent),
						}),
				certificateChoices: parseCertificateSet(der, optionalFields.value.certificates),
				signerInfos: parseSignerInfos(der, signerInfos),
			},
		};
	} catch {
		return pkcs7Failure('malformed', 'Malformed PKCS#7 structure');
	}
}

function parseSignedDataOptionalFields(
	children: readonly DerElement[],
):
	| { readonly ok: true; readonly value: ParsedSignedDataOptionalFields }
	| ErrorResult<ParsePkcs7ErrorCode, Record<never, never>, ParsePkcs7Failure> {
	let certificates: DerElement | undefined;
	let crls: DerElement | undefined;
	for (const child of children) {
		if (child.tag === 0xa0) {
			if (crls !== undefined)
				return pkcs7Failure('malformed', 'SignedData certificates field must precede CRLs field');
			if (certificates !== undefined)
				return pkcs7Failure('malformed', 'SignedData certificates field must not repeat');
			certificates = child;
			continue;
		}
		if (child.tag === 0xa1) {
			if (crls !== undefined)
				return pkcs7Failure('malformed', 'SignedData CRLs field must not repeat');
			crls = child;
			continue;
		}
		return pkcs7Failure('malformed', 'Malformed SignedData optional field');
	}
	return {
		ok: true,
		value: {
			...(certificates === undefined ? {} : { certificates }),
			...(crls === undefined ? {} : { crls }),
		},
	};
}

/** Decodes a PEM-armored PKCS#7/CMS SignedData. Expects exactly one `PKCS7` or `CMS` PEM block. */
export function parsePkcs7SignedDataPem(pem: string): ParsePkcs7SignedDataResult {
	const block = contentInfoPemBytes(pem);
	if (!block.ok) return block;
	return parsePkcs7SignedDataDer(block.bytes);
}

// verifyPkcs7SignedData

/**
 * Verifies all signer signatures in a PKCS#7 SignedData structure.
 *
 * Accepts PEM text, raw DER, or an already-parsed {@linkcode ParsedPkcs7SignedData}.
 * For each signer, locates the matching certificate in the embedded set and
 * verifies the signature (including signed-attribute digest checks per RFC 5652 Section 5.4).
 *
 * For a detached SignedData (absent `eContent`), pass the externally-held
 * content via `options.content`; without it, verification fails with the
 * typed `detached_content_required` code. When the SignedData embeds its own
 * content, that embedded content is verified and `options.content` is ignored.
 *
 * @example
 * ```ts
 * import { verifyPkcs7SignedData } from 'micro509';
 *
 * const result = await verifyPkcs7SignedData(pkcs7Pem);
 * if (result.ok) {
 *   for (const { signerInfo, certificate } of result.signers) {
 *     console.log(signerInfo.digestAlgorithmOid, certificate.subject);
 *   }
 * }
 *
 * // Detached signature: supply the content externally
 * const detached = await verifyPkcs7SignedData(cmsBlob, { content: signedBytes });
 * ```
 */
export async function verifyPkcs7SignedData(
	input: string | Uint8Array | ParsedPkcs7SignedData,
	options: VerifyPkcs7SignedDataOptions = {},
): Promise<VerifyPkcs7SignedDataResult> {
	const parsedResult = normalizePkcs7SignedDataForVerification(input);
	if (!parsedResult.ok) return parsedResult;
	const parsed = parsedResult.value;
	// Snapshot caller-owned external content before any await so every signer
	// verifies against the same bytes (mirrors the create-side content copy).
	const content = parsed.encapsulatedContent ?? options.content?.slice();
	if (content === undefined) {
		return verifyPkcs7Failure(
			'detached_content_required',
			'SignedData has no encapsulated content; supply the detached content via options.content',
		);
	}
	const signers: VerifiedPkcs7Signer[] = [];
	for (const signerInfo of parsed.signerInfos) {
		const result = await verifyPkcs7SignerInfo(parsed, signerInfo, content);
		if (!result.ok) return result;
		signers.push({ signerInfo, certificate: result.certificate });
	}
	return { ok: true, value: parsed, signers };
}

/** Normalizes encoded or parsed SignedData into a freshly parsed verification input. */
function normalizePkcs7SignedDataForVerification(
	input: string | Uint8Array | ParsedPkcs7SignedData,
): ParsePkcs7SignedDataResult | VerifyPkcs7SignedDataResult {
	if (typeof input === 'string') return parsePkcs7SignedDataPem(input);
	if (input instanceof Uint8Array) return parsePkcs7SignedDataDer(input);
	if (!hasReparseablePkcs7SignedData(input)) {
		return verifyPkcs7Failure('malformed', 'SignedData parsed input is malformed');
	}
	return parsePkcs7SignedDataDer(new Uint8Array(input.der));
}

/** Resolves and verifies one signer entry in a parsed PKCS#7 SignedData value. */
async function verifyPkcs7SignerInfo(
	parsed: ParsedPkcs7SignedData,
	signerInfo: ParsedPkcs7SignerInfo,
	content: Uint8Array,
): Promise<
	| { readonly ok: true; readonly certificate: ParsedCertificate }
	| Extract<VerifyPkcs7SignedDataResult, { readonly ok: false }>
> {
	const signer = x509CertificatesOf(parsed.certificateChoices).find((certificate) =>
		signerIdentifierMatches(certificate, signerInfo),
	);
	if (signer === undefined) {
		return verifyPkcs7Failure(
			'signer_not_found',
			'Signer certificate not found in SignedData certificates',
		);
	}
	const result = signerInfo.hasSignedAttrs
		? await verifySignedAttrs(signerInfo, signer, content, parsed.encapsulatedContentTypeOid)
		: await verifyPkcs7BareSignerInfo(signerInfo, signer, content);
	return result.ok ? { ok: true, certificate: signer } : result;
}

async function verifyPkcs7BareSignerInfo(
	signerInfo: ParsedPkcs7SignerInfo,
	signer: ParsedCertificate,
	content: Uint8Array,
): Promise<{ readonly ok: true } | Extract<VerifyPkcs7SignedDataResult, { readonly ok: false }>> {
	try {
		const verificationResult = await verifySignedDataDetailed(
			signerInfo.signatureAlgorithmOid,
			signerInfo.signatureAlgorithmParametersDer,
			signer.publicKeyAlgorithmOid,
			signer.publicKeyParametersOid,
			signer.subjectPublicKeyInfoDer,
			signerInfo.signature,
			content,
		);
		if (!verificationResult.ok) {
			return verificationResult.code === 'verification_error'
				? verifyPkcs7Failure('malformed', 'SignedData signature verification failed')
				: verifyPkcs7Failure('malformed', 'Unsupported signature algorithm in SignedData');
		}
		return verificationResult.valid
			? { ok: true }
			: verifyPkcs7Failure('signature_invalid', 'SignedData signature does not verify');
	} catch {
		return verifyPkcs7Failure('malformed', 'Unsupported signature algorithm in SignedData');
	}
}

// Private helpers

/** Shorthand for constructing a PKCS#7 parse failure result. */
function pkcs7Failure(
	code: ParsePkcs7ErrorCode,
	message: string,
): ErrorResult<ParsePkcs7ErrorCode, Record<never, never>, ParsePkcs7Failure> {
	return failureResult(code, message);
}

/** Shorthand for constructing a PKCS#7 verification failure result. */
function verifyPkcs7Failure(
	code: VerifyPkcs7SignedDataErrorCode,
	message: string,
): ErrorResult<VerifyPkcs7SignedDataErrorCode, Record<never, never>, VerifyPkcs7SignedDataFailure> {
	return failureResult(code, message);
}

/** Shorthand for constructing a PKCS#7 certificate bag creation failure result. */
function createCertBagFailure(
	code: CreatePkcs7CertBagErrorCode,
	message: string,
): ErrorResult<CreatePkcs7CertBagErrorCode, Record<never, never>, CreatePkcs7CertBagFailure> {
	return failureResult(code, message);
}

/** Shorthand for constructing a PKCS#7 SignedData creation failure result. */
function createPkcs7Failure(
	code: CreatePkcs7SignedDataErrorCode,
	message: string,
): ErrorResult<CreatePkcs7SignedDataErrorCode, Record<never, never>, CreatePkcs7SignedDataFailure> {
	return failureResult(code, message);
}

/** Converts PEM text to an array of DER certificate blobs, or wraps raw DER. */
function normalizeCertificateSource(source: Pkcs7CertificateSource): readonly Uint8Array[] {
	if (typeof source === 'string') {
		const blocks = splitPemBlocksOrThrow(source)
			.filter((block) => block.label === 'CERTIFICATE')
			.map((block) => new Uint8Array(block.bytes));
		if (blocks.length === 0) {
			throw new Error('Certificate PEM required');
		}
		return blocks;
	}
	if (source instanceof Uint8Array) {
		return [new Uint8Array(source)];
	}
	return [new Uint8Array(source.der)];
}

/** Type guard: key algorithm carries an RSA `hash`. */
function signerHasHash(algorithm: KeyAlgorithm): algorithm is RsaHashedKeyAlgorithm {
	return 'hash' in algorithm;
}

/** Type guard: key algorithm carries an EC `namedCurve`. */
function signerHasNamedCurve(algorithm: KeyAlgorithm): algorithm is EcKeyAlgorithm {
	return 'namedCurve' in algorithm;
}

/**
 * Resolves the content-digest hash and its OID for a signer key.
 *
 * ECDSA curve pairing follows RFC 5753 §8: P-256 → SHA-256, P-384 → SHA-384,
 * P-521 → SHA-512. RSA uses the hash bound to the key.
 * Digest OIDs are from RFC 5754 §2.
 * Ed25519 uses SHA-512 for the messageDigest attribute, per RFC 8419.
 */
function contentDigestForPrivateKey(
	privateKey: CryptoKey,
):
	| { readonly hashName: 'SHA-256' | 'SHA-384' | 'SHA-512'; readonly digestOid: string }
	| undefined {
	const algorithm = privateKey.algorithm;
	if (algorithm.name === 'ECDSA') {
		if (!signerHasNamedCurve(algorithm)) {
			return undefined;
		}
		switch (algorithm.namedCurve) {
			case 'P-256':
				return { hashName: 'SHA-256', digestOid: OIDS.sha256 };
			case 'P-384':
				return { hashName: 'SHA-384', digestOid: OIDS.sha384 };
			case 'P-521':
				return { hashName: 'SHA-512', digestOid: OIDS.sha512 };
			default:
				return undefined;
		}
	}
	if (algorithm.name === 'RSASSA-PKCS1-v1_5' || algorithm.name === 'RSA-PSS') {
		if (!signerHasHash(algorithm)) {
			return undefined;
		}
		switch (algorithm.hash.name) {
			case 'SHA-256':
				return { hashName: 'SHA-256', digestOid: OIDS.sha256 };
			case 'SHA-384':
				return { hashName: 'SHA-384', digestOid: OIDS.sha384 };
			case 'SHA-512':
				return { hashName: 'SHA-512', digestOid: OIDS.sha512 };
			default:
				return undefined;
		}
	}
	if (algorithm.name === 'Ed25519') {
		return { hashName: 'SHA-512', digestOid: OIDS.sha512 };
	}
	return undefined;
}

/**
 * Builds CMS signed attributes (contentType + messageDigest) in two forms:
 * `setForSigning` (SET OF, tag 0x31) is what the signature covers per RFC 5652
 * Section 5.4; `implicitForEmit` (IMPLICIT [0], tag 0xa0) is what goes in the
 * SignerInfo. The two differ only in the leading tag byte.
 */
function buildSignedAttributes(
	contentTypeOid: string,
	messageDigest: Uint8Array,
): { readonly setForSigning: Uint8Array; readonly implicitForEmit: Uint8Array } {
	const setForSigning = setOf([
		sequence([objectIdentifier(OIDS.cmsContentType), setOf([objectIdentifier(contentTypeOid)])]),
		sequence([objectIdentifier(OIDS.cmsMessageDigest), setOf([octetString(messageDigest)])]),
	]);
	const implicitForEmit = new Uint8Array(setForSigning);
	implicitForEmit[0] = 0xa0;
	return { setForSigning, implicitForEmit };
}

/** The X.509 subset of a decoded CertificateSet. */
function x509CertificatesOf(
	choices: readonly ParsedCertificateChoice[],
): readonly ParsedCertificate[] {
	return choices.flatMap((choice) => (choice.type === 'certificate' ? [choice.certificate] : []));
}

/**
 * `otherCertFormat` from an `other [3] IMPLICIT OtherCertificateFormat` entry.
 *
 * The IMPLICIT tag replaces the SEQUENCE tag, so the OID and the `ANY DEFINED BY`
 * value are direct children of the `[3]` element.
 */
function parseOtherCertificateFormatOid(source: Uint8Array, element: DerElement): string {
	const formatOid = requireElement(childrenOf(source, element)[0], 'otherCertFormat');
	if (formatOid.tag !== 0x06) {
		throw new Error('otherCertFormat must be an OBJECT IDENTIFIER');
	}
	return decodeObjectIdentifier(formatOid.value);
}

/** Decodes one RFC 5652 §10.2.2 CertificateChoices alternative. */
function parseCertificateChoice(
	source: Uint8Array,
	element: DerElement,
	der: Uint8Array,
): ParsedCertificateChoice {
	switch (element.tag) {
		case 0x30:
			return { type: 'certificate', certificate: parseCertificateDerOrThrow(der) };
		case 0xa0:
			return { type: 'extendedCertificate', der };
		case 0xa1:
			return { type: 'attributeCertificateV1', der };
		case 0xa2:
			return { type: 'attributeCertificateV2', der };
		case 0xa3:
			return {
				type: 'other',
				formatOid: parseOtherCertificateFormatOid(source, element),
				der,
			};
		default:
			throw new Error(`Unsupported CertificateChoices tag: ${element.tag}`);
	}
}

/**
 * Parses the IMPLICIT [0] certificate set from a SignedData structure as
 * RFC 5652 §10.2.3 `CertificateSet ::= SET OF CertificateChoices`.
 */
function parseCertificateSet(
	source: Uint8Array,
	certificates: ReturnType<typeof readElement> | undefined,
): readonly ParsedCertificateChoice[] {
	if (certificates === undefined || certificates.tag !== 0xa0) {
		return [];
	}
	return childrenOf(source, certificates).map((element) =>
		parseCertificateChoice(
			source,
			element,
			source.slice(element.start - element.headerLength, element.end),
		),
	);
}

/** Extracts the list of digest algorithm OIDs from the digestAlgorithms SET. */
function parseDigestAlgorithms(
	source: Uint8Array,
	element: ReturnType<typeof readElement>,
): readonly string[] {
	const digests: string[] = [];
	if (element.tag !== 0x31) {
		throw new Error('digestAlgorithms must use SET');
	}
	for (const child of childrenOf(source, element)) {
		if (child.tag !== 0x30) {
			throw new Error('digestAlgorithm must use AlgorithmIdentifier SEQUENCE');
		}
		const childDer = source.slice(child.start - child.headerLength, child.end);
		const parts = readSequenceChildren(childDer);
		const oid = parts[0];
		if (oid === undefined || parts.length < 1 || parts.length > 2 || oid.tag !== 0x06) {
			throw new Error('Malformed digest AlgorithmIdentifier');
		}
		digests.push(decodeObjectIdentifier(oid.value));
	}
	return digests;
}

/** Decodes the signerInfos SET OF from a SignedData structure. */
function parseSignerInfos(
	source: Uint8Array,
	element: ReturnType<typeof readElement>,
): readonly ParsedPkcs7SignerInfo[] {
	return childrenOf(source, element).map((signerInfo) => parseSignerInfo(source, signerInfo));
}

function parseSignerInfo(source: Uint8Array, signerInfo: DerElement): ParsedPkcs7SignerInfo {
	const signerDer = source.slice(signerInfo.start - signerInfo.headerLength, signerInfo.end);
	const parts = readSequenceChildren(signerDer);
	const version = parts[0];
	const sid = parts[1];
	const digestAlgorithm = parts[2];
	let index = 3;
	const signedAttrsElement = parts[index]?.tag === 0xa0 ? parts[index] : undefined;
	if (signedAttrsElement !== undefined) index += 1;
	const signatureAlgorithm = parts[index];
	const signature = parts[index + 1];
	const unauthenticatedAttributes = parts[index + 2];
	if (
		version === undefined ||
		sid === undefined ||
		digestAlgorithm === undefined ||
		signatureAlgorithm === undefined ||
		signature === undefined ||
		signature.tag !== 0x04 ||
		parts.length > index + 3 ||
		(unauthenticatedAttributes !== undefined && unauthenticatedAttributes.tag !== 0xa1)
	) {
		throw new Error('Malformed SignerInfo');
	}
	const digestAlgorithmOid = parsePkcs7AlgorithmOid(
		signerDer,
		digestAlgorithm,
		'digest algorithm OID',
	);
	const signatureAlgorithmDer = signerDer.slice(
		signatureAlgorithm.start - signatureAlgorithm.headerLength,
		signatureAlgorithm.end,
	);
	const signatureAlgorithmChildren = readSequenceChildren(signatureAlgorithmDer);
	const signatureAlgorithmOid = decodeObjectIdentifier(
		requireElement(signatureAlgorithmChildren[0], 'signature algorithm OID').value,
	);
	const signatureAlgorithmParams = signatureAlgorithmChildren[1];
	return {
		version: decodeIntegerNumber(version.value),
		signerIdentifier: parseSignerIdentifier(signerDer.slice(sid.start - sid.headerLength, sid.end)),
		digestAlgorithmOid,
		digestAlgorithmName: describeHashAlgorithm(digestAlgorithmOid),
		signatureAlgorithmOid,
		signatureAlgorithmName: describeSignatureAlgorithm(
			signatureAlgorithmOid,
			signatureAlgorithmParams === undefined
				? undefined
				: new Uint8Array(
						signatureAlgorithmDer.slice(
							signatureAlgorithmParams.start - signatureAlgorithmParams.headerLength,
							signatureAlgorithmParams.end,
						),
					),
		),
		...(signatureAlgorithmParams === undefined
			? {}
			: {
					signatureAlgorithmParametersDer: new Uint8Array(
						signatureAlgorithmDer.slice(
							signatureAlgorithmParams.start - signatureAlgorithmParams.headerLength,
							signatureAlgorithmParams.end,
						),
					),
				}),
		signatureHex: toHex(signature.value),
		signature: new Uint8Array(signature.value),
		...(signedAttrsElement === undefined
			? { hasSignedAttrs: false as const }
			: {
					hasSignedAttrs: true as const,
					signedAttrsDer: new Uint8Array(
						signerDer.slice(
							signedAttrsElement.start - signedAttrsElement.headerLength,
							signedAttrsElement.end,
						),
					),
				}),
	};
}

function parsePkcs7AlgorithmOid(source: Uint8Array, element: DerElement, label: string): string {
	const algorithmDer = source.slice(element.start - element.headerLength, element.end);
	return decodeObjectIdentifier(requireElement(readSequenceChildren(algorithmDer)[0], label).value);
}

/** Unwraps the OCTET STRING inside the IMPLICIT [0] encapsulated content. */
function extractEncapsulatedContent(
	encapDer: Uint8Array,
	element: ReturnType<typeof readElement>,
): Uint8Array {
	if (element.tag !== 0xa0) {
		throw new Error('Unexpected encapsulated content tag');
	}
	const inner = readElement(encapDer, element.start);
	if (inner.tag !== 0x04) {
		throw new Error('Expected encapsulated OCTET STRING');
	}
	if (inner.end !== element.end) {
		throw new Error('Encapsulated content must hold exactly one element');
	}
	return inner.value;
}

/** Extracts issuer Name and serial number from an issuerAndSerialNumber SEQUENCE, or subjectKeyIdentifier from [0] IMPLICIT. */
function parseSignerIdentifier(der: Uint8Array): ParsedSignerIdentifier {
	const element = readRootElement(der, { maxDepth: DEFAULT_MAX_DER_DEPTH });
	// [0] IMPLICIT SubjectKeyIdentifier
	if (element.tag === 0x80) {
		if (element.value.length === 0) {
			throw new Error('SignerIdentifier subjectKeyIdentifier must not be empty');
		}
		return {
			type: 'subjectKeyIdentifier',
			subjectKeyIdentifier: toHex(element.value),
		};
	}
	// SEQUENCE { issuer Name, serialNumber INTEGER }
	if (element.tag === 0x30) {
		const top = readSequenceChildren(der);
		const issuerElement = top[0];
		const serial = top[1];
		if (issuerElement === undefined || serial === undefined || top.length !== 2) {
			throw new Error('SignerIdentifier issuerAndSerialNumber is malformed');
		}
		if (issuerElement.tag !== 0x30) {
			throw new Error('SignerIdentifier issuer must use Name SEQUENCE');
		}
		if (serial.tag !== 0x02) {
			throw new Error('SignerIdentifier serialNumber must use INTEGER');
		}
		assertImplicitSerialNumberEncoding(serial.value, 'SignerIdentifier serialNumber');
		return {
			type: 'issuerAndSerialNumber',
			issuer: parseSignerIssuerName(der, issuerElement),
			serialNumberHex: toHex(serial.value),
		};
	}
	throw new Error(`Unsupported SignerIdentifier tag: ${String(element.tag)}`);
}

const textDecoder = new TextDecoder();

/** Parses a Name SEQUENCE element from a PKCS#7 signer identifier into a {@linkcode ParsedName}. */
function parseSignerIssuerName(source: Uint8Array, element: DerElement): ParsedName {
	const derHex = toHex(source.slice(element.start - element.headerLength, element.end));
	const rdns: ParsedRelativeDistinguishedName[] = [];
	const allAttributes: ParsedNameAttribute[] = [];
	const values: Partial<Record<NameFieldKey, string>> = {};
	for (const setElement of childrenOf(source, element)) {
		const rdnAttributes: ParsedNameAttribute[] = [];
		const rdnValues: Partial<Record<NameFieldKey, string>> = {};
		for (const attrSequence of childrenOf(source, setElement)) {
			const parts = childrenOf(source, attrSequence);
			const oidElement = requireElement(parts[0], 'signer issuer attribute OID');
			const valueElement = requireElement(parts[1], 'signer issuer attribute value');
			const oid = decodeObjectIdentifier(oidElement.value);
			let fieldValue: string;
			try {
				fieldValue = decodeString(valueElement.tag, valueElement.value);
			} catch {
				fieldValue = textDecoder.decode(valueElement.value);
			}
			const fieldKey = nameFieldKeyFromOid(oid);
			const attribute: ParsedNameAttribute =
				fieldKey !== undefined
					? { oid, key: fieldKey, valueTag: valueElement.tag, value: fieldValue }
					: { oid, valueTag: valueElement.tag, value: fieldValue };
			rdnAttributes.push(attribute);
			allAttributes.push(attribute);
			if (fieldKey !== undefined) {
				if (rdnValues[fieldKey] === undefined) {
					rdnValues[fieldKey] = fieldValue;
				}
				if (values[fieldKey] === undefined) {
					values[fieldKey] = fieldValue;
				}
			}
		}
		rdns.push({
			derHex: toHex(source.slice(setElement.start - setElement.headerLength, setElement.end)),
			attributes: rdnAttributes,
			values: rdnValues,
		});
	}
	return { derHex, rdns, attributes: allAttributes, values };
}

/** Returns the nth child element inside a constructed ASN.1 element, or throws. */
function childAt(source: Uint8Array, parent: DerElement, index: number, label: string): DerElement {
	let offset = parent.start;
	let currentIndex = 0;
	while (offset < parent.end) {
		const child = readElement(source, offset);
		if (currentIndex === index) {
			return child;
		}
		offset = child.end;
		currentIndex += 1;
	}
	throw new Error(`Missing ${label}`);
}

// CMS signed attributes verification (RFC 5652 Section 5.4)

/** Maps a digest algorithm OID to the WebCrypto hash name. */
function digestAlgorithmHash(digestAlgorithmOid: string): 'SHA-256' | 'SHA-384' | 'SHA-512' {
	switch (digestAlgorithmOid) {
		case OIDS.sha256:
			return 'SHA-256';
		case OIDS.sha384:
			return 'SHA-384';
		case OIDS.sha512:
			return 'SHA-512';
		default:
			throw new Error(`Unsupported digest algorithm OID: ${digestAlgorithmOid}`);
	}
}

/** Parses required signed attributes and enforces uniqueness + exact-one-value shape. */
function parseSignedAttributeRequirements(signedAttrsDer: Uint8Array): {
	readonly messageDigest: Uint8Array;
	readonly contentTypeOid: string;
} {
	const outer = readElement(signedAttrsDer);
	const fields: ParsedSignedAttributeFields = {};
	for (const attr of childrenOf(signedAttrsDer, outer)) {
		parseSignedAttributeRequirement(signedAttrsDer, attr, fields);
	}
	if (fields.messageDigest === undefined) {
		throw new Error('Missing messageDigest attribute in signedAttrs');
	}
	if (fields.contentTypeOid === undefined) {
		throw new Error('Missing contentType attribute in signedAttrs');
	}
	return { messageDigest: fields.messageDigest, contentTypeOid: fields.contentTypeOid };
}

function parseSignedAttributeRequirement(
	signedAttrsDer: Uint8Array,
	attr: DerElement,
	fields: ParsedSignedAttributeFields,
): void {
	const attrDer = signedAttrsDer.slice(attr.start - attr.headerLength, attr.end);
	const parts = readSequenceChildren(attrDer);
	const oid = parts[0];
	const values = parts[1];
	if (oid === undefined || values === undefined || parts.length !== 2 || values.tag !== 0x31) {
		throw new Error('Malformed signedAttrs attribute');
	}
	const attrOid = decodeObjectIdentifier(oid.value);
	const valueElements = childrenOf(attrDer, values);
	if (attrOid === OIDS.cmsMessageDigest) {
		fields.messageDigest = parseUniqueSignedAttributeOctetString(
			fields.messageDigest,
			valueElements,
			'messageDigest',
		);
	}
	if (attrOid === OIDS.cmsContentType) {
		fields.contentTypeOid = parseUniqueSignedAttributeOid(
			fields.contentTypeOid,
			valueElements,
			'contentType',
		);
	}
}

function parseUniqueSignedAttributeOctetString(
	existing: Uint8Array | undefined,
	valueElements: readonly DerElement[],
	label: string,
): Uint8Array {
	if (existing !== undefined || valueElements.length !== 1) {
		throw new Error(`${label} attribute must appear exactly once with one value`);
	}
	const value = requireElement(valueElements[0], `${label} attribute value`);
	if (value.tag !== 0x04) {
		throw new Error(`${label} attribute value must use OCTET STRING`);
	}
	return value.value;
}

function parseUniqueSignedAttributeOid(
	existing: string | undefined,
	valueElements: readonly DerElement[],
	label: string,
): string {
	if (existing !== undefined || valueElements.length !== 1) {
		throw new Error(`${label} attribute must appear exactly once with one value`);
	}
	const value = requireElement(valueElements[0], `${label} attribute value`);
	if (value.tag !== 0x06) {
		throw new Error(`${label} attribute value must use OBJECT IDENTIFIER`);
	}
	return decodeObjectIdentifier(value.value);
}

/** Replaces the IMPLICIT [0] tag (0xa0) with SET OF (0x31) per RFC 5652 Section 5.4. */
function retagSignedAttrsAsSet(signedAttrsDer: Uint8Array): Uint8Array {
	// Replace IMPLICIT [0] tag (0xa0) with SET OF tag (0x31) per RFC 5652 Section 5.4
	const copy = new Uint8Array(signedAttrsDer);
	copy[0] = 0x31;
	return copy;
}

/** Constant-time byte comparison to avoid timing side-channels in digest checks. */
function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
	if (a.length !== b.length) {
		return false;
	}
	let diff = 0;
	for (let index = 0; index < a.length; index += 1) {
		diff |= (a[index] ?? 0) ^ (b[index] ?? 0);
	}
	return diff === 0;
}

/** Verifies a signed-attributes flow: digest match + signature over re-tagged attrs. */
async function verifySignedAttrs(
	signerInfo: ParsedPkcs7SignerInfo,
	signer: ParsedCertificate,
	content: Uint8Array,
	encapsulatedContentTypeOid: string,
): Promise<
	| { readonly ok: true }
	| ErrorResult<VerifyPkcs7SignedDataErrorCode, Record<never, never>, VerifyPkcs7SignedDataFailure>
> {
	if (!signerInfo.hasSignedAttrs) {
		return verifyPkcs7Failure('malformed', 'Missing signedAttrs DER');
	}
	// Step 1: Parse required signed attributes from signedAttrs
	let signedAttributes: { readonly messageDigest: Uint8Array; readonly contentTypeOid: string };
	try {
		assertImplicitSignedAttrsDer(signerInfo.signedAttrsDer);
		signedAttributes = parseSignedAttributeRequirements(signerInfo.signedAttrsDer);
	} catch {
		return verifyPkcs7Failure('malformed', 'Malformed signedAttrs in SignedData');
	}
	if (signedAttributes.contentTypeOid !== encapsulatedContentTypeOid) {
		return verifyPkcs7Failure('malformed', 'SignedData contentType attribute does not match');
	}
	// Step 2: Compute digest of the content (embedded, or external for detached)
	let actualDigest: Uint8Array;
	try {
		const hash = digestAlgorithmHash(signerInfo.digestAlgorithmOid);
		actualDigest = new Uint8Array(await getCrypto().subtle.digest(hash, toArrayBuffer(content)));
	} catch {
		return verifyPkcs7Failure('malformed', 'Unsupported digest algorithm in SignedData');
	}
	// Step 3: Compare digests (constant-time)
	if (!constantTimeEqual(actualDigest, signedAttributes.messageDigest)) {
		return verifyPkcs7Failure(
			'message_digest_mismatch',
			'Content digest does not match messageDigest attribute',
		);
	}
	// Step 4: Verify signature over re-tagged signedAttrs (0xa0 → 0x31 SET OF)
	let signedData: Uint8Array;
	try {
		signedData = retagSignedAttrsAsSet(signerInfo.signedAttrsDer);
	} catch {
		return verifyPkcs7Failure('malformed', 'Malformed signedAttrs in SignedData');
	}
	let verified: boolean;
	try {
		const verificationResult = await verifySignedDataDetailed(
			signerInfo.signatureAlgorithmOid,
			signerInfo.signatureAlgorithmParametersDer,
			signer.publicKeyAlgorithmOid,
			signer.publicKeyParametersOid,
			signer.subjectPublicKeyInfoDer,
			signerInfo.signature,
			signedData,
		);
		if (!verificationResult.ok) {
			if (verificationResult.code === 'verification_error') {
				return verifyPkcs7Failure('malformed', 'SignedData signature verification failed');
			}
			return verifyPkcs7Failure('malformed', 'Unsupported signature algorithm in SignedData');
		}
		verified = verificationResult.valid;
	} catch {
		return verifyPkcs7Failure('malformed', 'Unsupported signature algorithm in SignedData');
	}
	if (!verified) {
		return verifyPkcs7Failure(
			'signature_invalid',
			'SignedData signature over signedAttrs does not verify',
		);
	}
	return { ok: true };
}

function signerIdentifierMatches(
	certificate: ParsedCertificate,
	signerInfo: ParsedPkcs7SignerInfo,
): boolean {
	const identifier = signerInfo.signerIdentifier;
	if (identifier.type === 'issuerAndSerialNumber') {
		return (
			certificate.serialNumberHex === identifier.serialNumberHex &&
			compareDistinguishedNames(certificate.issuer, identifier.issuer)
		);
	}
	return certificate.subjectKeyIdentifier === identifier.subjectKeyIdentifier;
}

function assertImplicitSignedAttrsDer(signedAttrsDer: Uint8Array): void {
	if (readElement(signedAttrsDer).tag !== 0xa0) {
		throw new Error('signedAttrs must use IMPLICIT [0] tag');
	}
}

function hasReparseablePkcs7SignedData(
	value: ParsedPkcs7SignedData,
): value is ParsedPkcs7SignedData & { readonly der: Uint8Array } {
	return 'der' in value && value.der instanceof Uint8Array;
}

function assertImplicitSerialNumberEncoding(bytes: Uint8Array, label: string): void {
	const first = bytes[0];
	if (first === undefined) {
		throw new Error(`${label} must not be empty`);
	}
	if ((first & 0x80) !== 0) {
		throw new Error(`${label} must be non-negative`);
	}
	if (bytes.length > 1 && first === 0 && ((bytes[1] ?? 0) & 0x80) === 0) {
		throw new Error(`${label} must use minimal encoding`);
	}
}
